import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import VideoUploadService, { UploadResult, UploadProgress } from '../services/videoUploadService';
import FolderManager from './FolderManager';
import apiService from '../services/apiService';
import localFileService from '../services/localFileService';

interface VideoUploadProps {
  initialFile?: File; // 🔧 新增：初始檔案
  autoUpload?: boolean; // 🔧 新增：自動上傳標誌
  onUploadSuccess?: (videoData: UploadResult) => void;
  onUploadError?: (error: string) => void;
}

interface FileInfo {
  name: string;
  uuidName: string;  // 🔧 添加 UUID 文件名字段
  size: string;
  contentType: string;
  timeCreated: string;
  updated: string;
  url: string;
}

const VideoUpload: React.FC<VideoUploadProps> = ({ 
  initialFile, 
  autoUpload = false, 
  onUploadSuccess, 
  onUploadError 
}) => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState<UploadResult[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 🔧 新增：成功訊息狀態
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  // 🔧 新增：檔案列表狀態
  const [fileList, setFileList] = useState<any[]>([]);
  // 🔧 新增：Firestore 影片記錄狀態
  const [firestoreVideos, setFirestoreVideos] = useState<any[]>([]);
  const [loadingFirestore, setLoadingFirestore] = useState(false);
  const [firestoreError, setFirestoreError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 驗證文件類型
      if (!file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
        setError('請選擇有效的影片或音訊文件');
        return;
      }
      
      // 🔧 移除 500MB 限制，支援大文件上傳
      const fileSizeMB = file.size / (1024 * 1024);
      console.log(`📁 選擇文件: ${file.name} (${fileSizeMB.toFixed(2)}MB)`);
      
      if (fileSizeMB > 100) { // 超過 100MB 顯示提示
        console.log(`⚠️ 大文件檢測: ${fileSizeMB.toFixed(2)}MB，將使用分塊上傳`);
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  // 處理文件選擇（來自 FolderManager）
  const handleFileSelect = (file: any) => {
    console.log('🎬 從 FolderManager 選擇文件:', file);
    // 這裡可以添加文件選擇的邏輯
  };

  // 處理文件夾選擇（來自 FolderManager）
  const handleFolderSelect = (folder: any) => {
    console.log('📁 從 FolderManager 選擇文件夾:', folder);
    // 這裡可以添加文件夾選擇的邏輯
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // 使用真實的上傳服務
      console.log('🎯 開始真實上傳...');
      
      const result = await VideoUploadService.uploadVideo(
        selectedFile,
        (progress: UploadProgress) => {
          setUploadProgress(progress.percentage);
        }
      );

      if (result.success) {
        onUploadSuccess?.(result);
        
        // 重置狀態
        setSelectedFile(null);
        setUploadProgress(0);
        
        // 顯示成功消息
        console.log('🎉 真實上傳成功:', result);
        
        // 🔧 新增：上傳成功後立即保存到 Firestore
        try {
          await saveToFirestore(result);
          console.log('✅ 已成功保存到 Firestore');
        } catch (firestoreError) {
          console.error('❌ 保存到 Firestore 失敗:', firestoreError);
          // 即使 Firestore 保存失敗，也不影響上傳成功的狀態
        }
        
        // 上傳成功後自動刷新文件列表
        await fetchFileList();
      } else {
        throw new Error(result.error || '上傳失敗');
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '上傳失敗';
      setError(errorMessage);
      onUploadError?.(errorMessage);
      console.error('上傳錯誤:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 生成縮圖
  const generateThumbnail = async (filename: string, contentType: string) => {
    try {
      const result = await apiService.post('/api/generate-thumbnail', {
        filename,
        contentType
      });

      if (!result.success) {
        throw new Error(result.error || '生成縮圖失敗');
      }

      console.log('✅ 成功生成縮圖:', result);
      return result.thumbnailUrl;
    } catch (error) {
      console.error('❌ 生成縮圖失敗:', error);
      return null;
    }
  };

  // 分配文件到上傳文件夾
  const assignFileToUploadFolder = async (filename: string, originalFilename: string) => {
    try {
      const result = await apiService.post('/api/assign-file-to-upload-folder', {
        filename,
        originalFilename
      });

      if (!result.success) {
        throw new Error(result.error || '分配文件到文件夾失敗');
      }

      console.log('✅ 成功分配文件到文件夾:', result);
      return result;
    } catch (error) {
      console.error('❌ 分配文件到文件夾失敗:', error);
      throw error;
    }
  };

  // 保存到 Firestore
  const saveToFirestore = async (uploadResult: UploadResult) => {
    try {
      const videoData = {
        url: uploadResult.url,
        filename: uploadResult.filename,
        metadata: {
          size: uploadResult.metadata?.size || 0,
          type: uploadResult.metadata?.type || 'video/mp4',
          originalName: uploadResult.originalFilename || uploadResult.filename
        }
      };

      // 使用 API 服務調用後端 API 保存到 Firestore
      const result = await apiService.post('/api/save-video-to-firestore', videoData);

      if (!result.success) {
        throw new Error(result.error || '保存到 Firestore 失敗');
      }

      console.log('✅ 成功保存到 Firestore:', result);

      // 🔧 修復：縮圖生成失敗不影響上傳
      let thumbnailUrl: string | null = null;
      try {
        if (uploadResult.filename) {
          thumbnailUrl = await generateThumbnail(uploadResult.filename, uploadResult.metadata?.type || 'video/mp4');
          console.log('✅ 已成功生成縮圖');
        }
      } catch (thumbnailError) {
        console.warn('⚠️ 生成縮圖失敗，但不影響上傳流程:', thumbnailError);
        // 即使縮圖生成失敗，也不影響 Firestore 保存成功的狀態
      }

      // 🔧 新增：分配文件到上傳文件夾
      try {
        if (uploadResult.filename) {
          const originalFilename = uploadResult.originalFilename || uploadResult.filename;
          await assignFileToUploadFolder(uploadResult.filename, originalFilename);
          console.log('✅ 已成功分配文件到文件夾');
        }
      } catch (assignError) {
        console.error('❌ 分配文件到文件夾失敗:', assignError);
        // 即使分配失敗，也不影響 Firestore 保存成功的狀態
      }

      // 🔧 新增：記錄到本地檔案服務
      try {
        if (uploadResult.filename) {
          await localFileService.addUploadedFile({
            filename: uploadResult.filename,
            originalFilename: uploadResult.originalFilename || uploadResult.filename,
            fileSize: uploadResult.metadata?.size || 0,
            contentType: uploadResult.metadata?.type || 'video/mp4',
            uploadTime: Date.now(),
            thumbnailUrl: thumbnailUrl || undefined
          });
          console.log('✅ 已記錄到本地檔案服務');
        }
      } catch (localError) {
        console.error('❌ 記錄到本地檔案服務失敗:', localError);
        // 即使記錄失敗，也不影響上傳成功的狀態
      }

      return result;
    } catch (error) {
      console.error('❌ 保存到 Firestore 失敗:', error);
      throw error;
    }
  };

  // 獲取文件列表
  const fetchFileList = async () => {
    setLoadingFiles(true);
    setFilesError(null);
    
    try {

      const data = await apiService.get('/api/list-files');
      if (data.success) {
        setFileList(data.files);
      } else {
        throw new Error(data.error || '獲取文件列表失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '獲取文件列表失敗';
      setFilesError(errorMessage);
      console.error('獲取文件列表錯誤:', err);
    } finally {
      setLoadingFiles(false);
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes: string) => {
    const size = parseInt(bytes);
    if (size === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 格式化時間
  const formatTime = (timeString: string) => {
    try {
      const date = new Date(timeString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return timeString;
    }
  };

  // 刪除文件
  // 跳轉到 MainPage 並載入上傳的影片
  const handlePlayVideo = (file: FileInfo) => {
    console.log('🎬 準備跳轉到 MainPage 播放影片:', file.name);
    console.log('🔍 FileInfo 對象詳情:', {
      name: file.name,
      uuidName: file.uuidName,
      url: file.url,
      size: file.size,
      contentType: file.contentType,
      timeCreated: file.timeCreated
    });
    
    // 直接跳轉到 MainPage，使用 Video Tab 播放上傳的 GCS 影片
    navigate('/mainpage', { 
      state: { 
        // 使用 Video Tab 的格式
        googleDriveUrl: file.url,        // GCS URL
        googleDriveTitle: file.name,     // 文件名作為標題
        googleDrivePath: file.name,      // 文件名作為路徑
        fromUpload: true,                // 標記來自上傳
        uploadedFile: {
          name: file.name,               // 原始文件名
          uuidName: file.uuidName,       // UUID 文件名
          size: file.size,               // 文件大小
          contentType: file.contentType, // 文件類型
          timeCreated: file.timeCreated  // 創建時間
        },
        // 強制使用 Video Tab
        forceVideoTab: true,
        // 設置預設 Tab
        defaultTab: 'video'
      } 
    });
  };

  const handleDeleteFile = async (file: any) => {
    const displayName = file.name || '未知文件';
    if (!window.confirm(`確定要刪除文件 "${displayName}" 嗎？此操作無法撤銷。`)) {
      return;
    }

    try {
      // 使用 UUID 文件名進行刪除
      const uuidName = file.uuidName || file.name;
      const response = await fetch(`/api/gcs/delete-file/${encodeURIComponent(uuidName)}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('文件刪除成功:', displayName);
          // 重新獲取文件列表
          await fetchFileList();
        } else {
          throw new Error(data.error || '刪除失敗');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '刪除文件失敗';
      console.error('刪除文件錯誤:', err);
      alert(`刪除失敗: ${errorMessage}`);
    }
  };

  // 🔧 新增：自動上傳邏輯
  useEffect(() => {
    if (initialFile && autoUpload) {
      console.log('🔍 VideoUpload: 檢測到初始檔案，開始自動上傳:', initialFile.name);
      setSelectedFile(initialFile);
      handleUpload(initialFile);
    }
  }, [initialFile, autoUpload]);

  // 組件加載時獲取文件列表
  useEffect(() => {
    fetchFileList();
  }, []);

  // 🔧 新增：獲取 Firestore 中的影片記錄
  const fetchFirestoreVideos = async (forceRefresh = false) => {
    setLoadingFirestore(true);
    setFirestoreError(null);
    
    try {
      console.log('🔍 查詢 Firestore 中的上傳影片...');
      
      // 🔧 添加強制刷新機制
      if (forceRefresh) {
        console.log('🔄 強制刷新模式，清除緩存...');
        // 清除本地狀態
        setFirestoreVideos([]);
      }
      
      // 🔧 使用後端 API 而不是直接查詢 Firestore
      const response = await fetch('/api/get-uploaded-videos');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || '查詢失敗');
      }
      
      const videos = result.videos || [];
      console.log(`📊 從後端 API 查詢到 ${videos.length} 個影片記錄`);
      
      // 🔍 調試：顯示查詢到的記錄詳情
      if (videos.length > 0) {
        console.log('🔍 查詢到的記錄詳情:');
        videos.forEach((video: any, index: number) => {
          console.log(`  ${index + 1}. ID: ${video.id}, 標題: ${video.title}, source: ${video.source}`);
        });
      }
      
      setFirestoreVideos(videos);
    } catch (error) {
      console.error('❌ 查詢 Firestore 影片失敗:', error);
      setFirestoreError(error instanceof Error ? error.message : '查詢失敗');
    } finally {
      setLoadingFirestore(false);
    }
  };

  // 🔧 新增：刪除單個 Firestore 影片記錄
  const handleDeleteFirestoreVideo = async (video: any) => {
    if (!confirm(`確定要刪除影片 "${video.title}" 嗎？此操作將永久刪除影片記錄，無法撤銷。`)) {
      return;
    }
    
    try {
      console.log('🗑️ 準備刪除 Firestore 影片記錄:', video.id);
      
      // 🔧 重要：使用後端的 Firebase 配置，而不是前端的配置
      const response = await fetch('/api/delete-video-record', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: video.id,
          gcsFileName: video.gcsFileName,
          originalFileName: video.originalFileName
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || '刪除 Firestore 記錄失敗');
      }

      console.log('✅ 已成功刪除 Firestore 影片記錄:', video.title);
      
      // 3. 顯示成功消息
      alert(`影片 "${video.title}" 已成功刪除`);
      
      // 🔧 重要：刪除完成後立即重新查詢 Firestore，確保數據同步
      console.log('🔄 單個刪除完成，重新查詢 Firestore 數據...');
      await fetchFirestoreVideos(true); // 強制刷新
      
    } catch (error) {
      console.error('❌ 刪除 Firestore 影片記錄失敗:', error);
      alert(`刪除失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  // 🔧 移除：組件加載時不需要獲取 Firestore 影片記錄
  // useEffect(() => {
  //   fetchFirestoreVideos();
  // }, []);

    return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* 標籤頁 */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="影片上傳" icon={<CloudUploadIcon />} />
          <Tab label="檔案管理" icon={<FolderIcon />} />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <>
          {/* 上傳區域 */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CloudUploadIcon
            sx={{
              fontSize: 32,
              color: selectedFile ? 'primary.main' : 'text.secondary',
              mb: 1
            }}
          />
          
          <Typography variant="h6" gutterBottom>
            上傳影片
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            選擇要上傳的影片或音訊文件
          </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,audio/*"
          onChange={handleFileInputSelect}
          style={{ display: 'none' }}
        />

        <Button
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          sx={{ mb: 1 }}
          size="small"
        >
          選擇文件
        </Button>

        {selectedFile && (
          <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" gutterBottom>
              已選擇: {selectedFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              大小: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
            </Typography>
            
            <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading}
                startIcon={<CloudUploadIcon />}
                size="small"
              >
                {uploading ? '上傳中...' : '開始上傳'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleRemoveFile}
                disabled={uploading}
                color="error"
                size="small"
              >
                移除
              </Button>
            </Box>
          </Box>
        )}

        {uploading && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ mb: 0.5 }}
            />
            <Typography variant="caption">
              上傳進度: {uploadProgress}%
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
        </Paper>

      {/* 文件列表區域 */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            我的上傳文件
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="權限說明">
              <IconButton size="small" color="info">
                <InfoIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchFileList}
              disabled={loadingFiles}
            >
              刷新
            </Button>
          </Box>
        </Box>

        {/* 權限說明 */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>權限說明：</strong> 由於存儲桶安全設置，文件通過預簽名 URL 訪問。
            上傳的文件會自動生成唯一文件名，確保安全性。
          </Typography>
        </Alert>

        {/* 文件列表表格 */}
        {loadingFiles ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              載入文件列表中...
            </Typography>
          </Box>
        ) : filesError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {filesError}
          </Alert>
        ) : fileList.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              暫無上傳文件
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
                              <TableHead>
                  <TableRow>
                    <TableCell>縮圖</TableCell>
                    <TableCell>文件名</TableCell>
                    <TableCell>文件大小</TableCell>
                    <TableCell>上傳時間</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {fileList.map((file, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      {/* 縮圖顯示 */}
                      <Box 
                        sx={{ 
                          width: 80, 
                          height: 45, 
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                      >
                        {file.contentType?.startsWith('video/') ? (
                          <Box
                            component="video"
                            src={file.url}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 1
                            }}
                            onLoadStart={() => {
                              // 影片載入開始時顯示預設圖標
                            }}
                          />
                        ) : file.contentType?.startsWith('audio/') ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%'
                            }}
                          >
                            <PlayArrowIcon sx={{ fontSize: 24, color: 'grey.600' }} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%'
                            }}
                          >
                            <InsertDriveFileIcon sx={{ fontSize: 24, color: 'grey.600' }} />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        noWrap 
                        sx={{ 
                          maxWidth: 200,
                          color: 'primary.main',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.dark'
                          }
                        }}
                        onClick={() => handlePlayVideo(file)}
                      >
                        {file.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={formatFileSize(file.size)} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatTime(file.timeCreated)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="在 CheckPlay 中播放">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handlePlayVideo(file)}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="下載文件">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => window.open(file.url, '_blank')}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="刪除文件">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteFile(file)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* 文件統計信息 */}
        {fileList.length > 0 && (
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              共 {fileList.length} 個文件
            </Typography>
          </Box>
        )}
        </Paper>
      </>
      )}

      {activeTab === 1 && (
        <FolderManager
          onFileSelect={handleFileSelect}
          onFolderSelect={handleFolderSelect}
        />
      )}
    </Box>
  );
};

export default VideoUpload;